import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Star, Send, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const STAR_LABELS = [
  "",
  "Not what I needed",
  "Light value",
  "Useful",
  "Strong",
  "Genuinely sharp",
];

export default function Feedback() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | form | sending | done | error
  const [meta, setMeta] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [allowPublic, setAllowPublic] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/feedback/${token}`);
        setMeta(data);
        if (data.submitted) {
          setRating(data.rating || 0);
          setComment(data.comment || "");
          setName(data.name || "");
          setRole(data.role || "");
          setIsAnonymous(!!data.is_anonymous);
          setAllowPublic(!!data.allow_public);
        }
        setPhase("form");
      } catch (e) {
        const detail = e?.response?.data?.detail || "Could not load this feedback link.";
        setError(detail);
        setPhase("error");
      }
    })();
  }, [token]);

  const submit = async () => {
    if (rating < 1) return toast.error("Please pick a star rating.");
    try {
      setPhase("sending");
      await api.post(`/feedback/${token}`, {
        rating,
        comment: comment.trim() || null,
        name: isAnonymous ? null : (name.trim() || null),
        role: role.trim() || null,
        is_anonymous: isAnonymous,
        allow_public: allowPublic,
      });
      setPhase("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit your rating.");
      setPhase("form");
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
      <Header minimal />
      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-16">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Rate your plan</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          {phase === "done" ? "Thank you — that helps a lot" : "How did your resilience plan land?"}
        </h1>

        {phase === "loading" && (
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        )}

        {phase === "error" && (
          <div data-testid="feedback-error" className="mt-6 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4">
            <p className="text-sm text-slate-800 dark:text-slate-100">{error}</p>
          </div>
        )}

        {(phase === "form" || phase === "sending") && meta && (
          <>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Your honest 30-second feedback shapes the next plan for someone like you.
              {meta.organization ? <> · Plan prepared for <b>{meta.organization}</b>.</> : null}
            </p>
            {meta.submitted && (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                You've rated this before — feel free to update your answers.
              </p>
            )}

            <div className="mt-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 space-y-5">
              {/* Stars */}
              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Rating</label>
                <div data-testid="feedback-stars" className="mt-2 flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const active = (hover || rating) >= n;
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                        data-testid={`feedback-star-${n}`}
                        className="p-1 transition-transform hover:scale-110"
                      >
                        <Star
                          className={`h-7 w-7 ${active ? "fill-amber-400 text-amber-500" : "text-slate-300 dark:text-slate-600"}`}
                        />
                      </button>
                    );
                  })}
                </div>
                {(hover || rating) > 0 && (
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{STAR_LABELS[hover || rating]}</p>
                )}
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">A short comment (optional)</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder="What was useful? What was missing? Anything we should sharpen?"
                  data-testid="feedback-comment"
                  className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                />
              </div>

              <label className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-200 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                  data-testid="feedback-anonymous"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <span>Keep me anonymous. Show only my role &amp; industry — never my name or company.</span>
              </label>

              <div className={`grid sm:grid-cols-2 gap-4 transition-opacity ${isAnonymous ? "opacity-40 pointer-events-none" : ""}`}>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Display name — shown exactly as you type it</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isAnonymous}
                    placeholder="e.g. Jane D. or Marcelle"
                    data-testid="feedback-name"
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Role &amp; org (optional)</label>
                  <input
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Founder, Northwind Logistics"
                    data-testid="feedback-role"
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                </div>
              </div>

              <label className="flex items-start gap-2.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allowPublic}
                  onChange={(e) => setAllowPublic(e.target.checked)}
                  data-testid="feedback-allow-public"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <span>
                  I consent to SmartNote2Self displaying my rating &amp; comment publicly (website, marketing),
                  shown {isAnonymous ? <b>anonymously (role &amp; industry only)</b> : <>as <b>{name.trim() || "the display name above"}</b></>}.
                  You'll only publish it after review, and I can ask you to remove it at any time.
                </span>
              </label>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed">
                Your rating helps improve the tool even if you don't consent to public use. We never publish
                your company name for an anonymous testimonial. See our <a href="/privacy" className="underline">privacy policy</a>.
              </p>

              <button
                onClick={submit}
                disabled={phase === "sending"}
                data-testid="feedback-submit"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50"
              >
                {phase === "sending"
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                  : <>{meta?.submitted ? "Update rating" : "Submit rating"} <Send className="h-4 w-4" /></>}
              </button>
            </div>
          </>
        )}

        {phase === "done" && (
          <div data-testid="feedback-done" className="mt-8 rounded-xl border border-teal-300 dark:border-teal-500/40 bg-teal-50 dark:bg-teal-500/10 p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-teal-700 dark:text-teal-300 mt-0.5" />
              <div>
                <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">Rating recorded</p>
                <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                  This kind of signal is what makes the plans sharper over time. Genuinely — thank you.
                </p>
              </div>
            </div>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Visit SmartNote2Self <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
